/*
 * Cordial-IA-2026-07-29 - Regroupement visuel "Coffret Trio" dans le tiroir panier
 * (retour test Anthony : les bouteilles du trio se confondaient avec des bouteilles
 * achetees seules, la ligne emballage 0 EUR avait un stepper, et chaque croix
 * pouvait desagreger le coffret en silence).
 *
 * Le Liquid (snippets/cart-drawer.liquid) rend les lignes du groupe (bouteilles
 * portant la property "Coffret Trio" + ligne emballage SKU COFFRET-TRIO-EMBALLAGE)
 * comme UN bloc <cordial-trio-cart> sans stepper ni croix par sous-ligne. Ce
 * script gere l'UNIQUE action du bloc : "Retirer le coffret" = toutes les lignes
 * du groupe a 0 en un seul POST /cart/update.js, updates par line item KEY et
 * JAMAIS par variant id (une bouteille seule du meme variant est une AUTRE ligne
 * - properties differentes - et ne doit pas etre touchee). Source verifiee :
 * shopify.dev/docs/api/ajax/reference/cart ("updates ... the line item's key",
 * bundled section rendering disponible sur /cart/update), consulte 2026-07-29.
 *
 * Patron aligne sur component-cart-quantity.js / cordial-gift-threshold.js :
 * sections=cart-drawer dans le POST puis eventBus 'update:cart:drawer' avec la
 * reponse (le tiroir remplace ses zones data-cart-dynamic-content). Custom
 * element : se re-branche seul a chaque re-rendu innerHTML du tiroir, comme
 * <cart-quantity>. Anti-double-clic par verrou + disabled. Les lignes cadeaux
 * mignonnette/jigger ne sont JAMAIS touchees ici : cordial-gift-threshold.js
 * les reconcilie seul si le sous-total change.
 *
 * FILET DE RE-RENDU : cordial-gift-threshold.js emet 'update:cart:drawer' SANS
 * sections (le tiroir ignore cet event pour le rendu - comportement pre-existant).
 * Si le retrait du coffret fait retomber le panier sous 59/72 EUR, les lignes
 * cadeaux retirees resteraient affichees. On ecoute donc les events sans
 * sections et on demande un 'render:cart:drawer' (re-fetch Section Rendering
 * API) debounce. Pas de boucle possible : render:cart:drawer ne re-emet rien
 * cote tiroir, et la reconciliation cadeaux n'emet que si elle change le panier
 * (un panier stable redevient silencieux).
 *
 * Cordial-IA-2026-07-29 (iteration 2, retour test Anthony : 3 trios empiles en
 * un seul bloc) : RETRAIT SCOPED PAR COFFRET. Le Liquid rend un bloc par valeur
 * de property ("Coffret n° N") et pose cette valeur en data-trio-group sur
 * chaque <cordial-trio-cart>. Le retrait ne met a 0 que les lignes dont le
 * label calcule (meme regle que le Liquid : valeur contenant "Coffret n°" =
 * elle-meme, sinon sentinelle retrocompat "__legacy__") correspond au groupe
 * du bloc clique. Les autres coffrets du panier ne sont jamais touches.
 *
 * Constantes partagees (ne pas renommer sans synchroniser) :
 *  - property key "Coffret Trio"  : ecrite par assets/cordial-trio.js, lue par
 *    snippets/cart-drawer.liquid et ici.
 *  - prefixe de valeur "Coffret n°" + sentinelle "__legacy__" : memes regles de
 *    groupement dans cordial-trio.js, cart-drawer.liquid et ici.
 *  - SKU "COFFRET-TRIO-EMBALLAGE" : produit emballage 0 EUR (draft jusqu'au
 *    go-live), lu par snippets/cart-drawer.liquid et ici.
 *
 * A11y : aria-busy pendant l'operation, erreur FR dans un role="alert", focus
 * repris par le trapFocus du tiroir apres re-rendu (le bloc disparait du DOM).
 * Zero dependance, defer, fail-safe : toute erreur reactive le bouton, le
 * panier n'est jamais laisse dans un etat intermediaire (updates atomiques).
 */
(() => {
  const TRIO_PROPERTY_KEY = 'Coffret Trio';
  const TRIO_PACKAGING_SKU = 'COFFRET-TRIO-EMBALLAGE';
  const TRIO_NUM_PREFIX = 'Coffret n°';
  const TRIO_LEGACY_GROUP = '__legacy__';

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.replace(/\/$/, '');
  };

  // Label de groupe d'une ligne de panier (MEME regle que le Liquid du tiroir) :
  //  - pas membre du trio (ni property "Coffret Trio", ni SKU emballage) -> null
  //  - valeur de property contenant "Coffret n°" -> la valeur elle-meme
  //  - sinon (anciennes valeurs, emballage sans property) -> sentinelle legacy
  const groupLabelFor = (item) => {
    const isPackaging = item.sku === TRIO_PACKAGING_SKU;
    const hasProp = !!(item.properties && Object.prototype.hasOwnProperty.call(item.properties, TRIO_PROPERTY_KEY));
    if (!isPackaging && !hasProp) return null;
    if (hasProp) {
      const val = item.properties[TRIO_PROPERTY_KEY];
      if (typeof val === 'string' && val.indexOf(TRIO_NUM_PREFIX) !== -1) return val;
    }
    return TRIO_LEGACY_GROUP;
  };

  class CordialTrioCart extends HTMLElement {
    connectedCallback() {
      this.busy = false;
      // Groupe du bloc, pose par le Liquid (valeur "Coffret n° N" ou sentinelle
      // legacy). Absent = bloc retrocompat.
      this.group = this.dataset.trioGroup || TRIO_LEGACY_GROUP;
      this.removeBtn = this.querySelector('[data-trio-cart-remove]');
      this.errorEl = this.querySelector('[data-trio-cart-error]');
      if (this.removeBtn) this.removeBtn.addEventListener('click', () => this.removeGroup());
    }

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.hidden = false;
    }

    async removeGroup() {
      if (this.busy) return; // anti double-clic (en plus du disabled)
      this.busy = true;
      if (this.removeBtn) {
        this.removeBtn.disabled = true;
        this.removeBtn.setAttribute('aria-busy', 'true');
      }
      if (this.errorEl) this.errorEl.hidden = true;

      // Cordial-IA-2026-07-31 - Resilience aux turbulences (rate-limit transitoire
      // constate en session de test) : une 2e tentative automatique apres une courte
      // pause avant d'afficher une erreur. La sequence elle-meme est verifiee
      // correcte (test API reel du 31/07 : add + update par keys + restore, 200).
      try {
        try {
          await this.attemptRemove();
          return;
        } catch (firstError) {
          await new Promise((resolve) => setTimeout(resolve, 1200));
          await this.attemptRemove();
          return;
        }
      } catch (e) {
        if (e && e.status === 429) {
          this.showError('Une seconde, s’il vous plaît : votre panier vient de recevoir plusieurs actions d’un coup. Patientez quelques instants, puis réessayez.');
        } else {
          this.showError('Le retrait du coffret a échoué. Rechargez la page et réessayez.');
        }
        if (this.removeBtn) {
          this.removeBtn.disabled = false;
          this.removeBtn.removeAttribute('aria-busy');
        }
      } finally {
        this.busy = false;
      }
    }

    async attemptRemove() {
      {
        // Etat REEL du panier au moment du clic - jamais des keys rendues cote
        // serveur, qui peuvent etre perimees si le panier a change entre-temps.
        const cartRes = await fetch(rootUrl() + '/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!cartRes.ok) {
          const fetchErr = new Error('cart fetch failed');
          fetchErr.status = cartRes.status;
          throw fetchErr;
        }
        const cart = await cartRes.json();

        // Scoped : uniquement les lignes de CE coffret (bouteilles + emballage
        // du meme numero). Les autres coffrets et les lignes normales du panier
        // ne sont jamais touches.
        const updates = {};
        for (const item of cart.items) {
          if (groupLabelFor(item) === this.group) updates[item.key] = 0;
        }

        if (!Object.keys(updates).length) {
          // Plus rien a retirer (panier modifie dans un autre onglet) :
          // on resynchronise simplement l'affichage du tiroir.
          if (window.eventBus) window.eventBus.emit('render:cart:drawer');
          return;
        }

        const res = await fetch(rootUrl() + '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ updates, sections: 'cart-drawer' }),
        });
        if (!res.ok) {
          const updErr = new Error('cart update failed');
          updErr.status = res.status;
          throw updErr;
        }
        const json = await res.json();

        if (window.eventBus) window.eventBus.emit('update:cart:drawer', json);
        // Compteur du header : meme patron que cart-quantity (la reponse
        // /cart/update.js est un objet cart complet, item_count inclus).
        if (window.wetheme && typeof window.wetheme.updateCartCount === 'function') {
          window.wetheme.updateCartCount(json);
        }
        // Pas de reactivation du bouton : le bloc disparait au re-rendu.
        // Les erreurs REMONTENT a removeGroup (retry automatique puis message).
      }
    }
  }

  if (!customElements.get('cordial-trio-cart')) {
    customElements.define('cordial-trio-cart', CordialTrioCart);
  }

  /* ===== Filet de re-rendu (cf. en-tete) ===== */
  let renderTimer = null;
  const scheduleRender = () => {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(() => {
      if (window.eventBus && document.querySelector('cart-drawer')) {
        window.eventBus.emit('render:cart:drawer');
      }
    }, 250);
  };

  const subscribe = () => {
    if (!window.eventBus) return false;
    window.eventBus.on('update:cart:drawer', (e) => {
      // Event porteur de sections : le tiroir vient de se re-rendre, rien a faire.
      if (e && e.sections && e.sections['cart-drawer']) return;
      scheduleRender();
    });
    return true;
  };

  // eventBus peut ne pas etre pret immediatement : on retente brievement
  // (meme patron que cordial-gift-threshold.js).
  if (!subscribe()) {
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (subscribe() || tries > 20) clearInterval(iv);
    }, 250);
  }
})();
